import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/userAuth";
import { useFeatureFlag } from "@/composables/useFeatureFlag";
import { handleDashboardRouting } from "@/router/guards/dashboard";

const router = createRouter({
    history: createWebHistory(),
    routes: [
        {
            path: "/",
            name: "Home Page",
            redirect: "/signin",
        },
        {
            path: "/signup/coming-soon",
            name: "coming-soon",
            component: () => import("@/views/common/ComingSoonView.vue"),
            // We only want to show this page after a user has registered with a country
            // not being GB and signing in anyways
        },
        {
            path: "/signin",
            name: "Signin",
            component: () => import("@/views/common/SigninView.vue"),
            meta: { requiresunAuth: true },
        },
        {
            path: "/clear-cookies",
            name: "Clear Cookies",
            component: () => import("@/views/common/ClearCookiesView.vue"),
            // No auth meta - intentionally public so a stuck user can break out of the redirect loop
        },
        {
            path: "/password-reset",
            name: "Reset Password Init",
            component: () => import("@/views/common/ResetPasswordInit.vue"),
            meta: { requiresunAuth: true },
        },
        {
            path: "/password-reset/commit/:resetToken",
            name: "Reset Password Commit",
            component: () => import("@/views/common/ResetPasswordCommit.vue"),
            props: true,
            meta: { requiresunAuth: true },
        },
        {
            path: "/password-reset/commit-onboarding/:resetToken",
            name: "Reset Password Onboarding Commit",
            component: () =>
                import("@/views/common/ResetPasswordOnboarding.vue"),
            props: true,
            meta: { requiresunAuth: true },
        },
        {
            path: "/accept-invite/:inviteId",
            name: "Accept Invite",
            component: () => import("@/views/common/AcceptInvite.vue"),
            props: true,
            meta: { requiresunAuth: true },
        },
        {
            path: "/onboarding/:uuid",
            name: "Public Buyer Onboarding",
            component: () => import("@/views/public/BuyerOnboardingView.vue"),
            props: true,
            // No meta tag - accessible to everyone (authenticated or not)
        },
        {
            path: "/onboarding/:uuid/details",
            name: "Public Buyer Onboarding Details",
            component: () =>
                import("@/views/public/BuyerOnboardingDetailsView.vue"),
            props: true,
            // No meta tag - accessible to everyone (authenticated or not)
        },
        {
            path: "/onboarding/:uuid/delivery",
            name: "Public Buyer Onboarding Delivery",
            component: () =>
                import("@/views/public/BuyerOnboardingDeliveryView.vue"),
            props: true,
            // No meta tag - accessible to everyone (authenticated or not)
        },
        {
            path: "/onboarding/:uuid/documents",
            name: "Public Buyer Onboarding Documents",
            component: () =>
                import("@/views/public/BuyerOnboardingDocumentsView.vue"),
            props: true,
            // No meta tag - accessible to everyone (authenticated or not)
        },
        {
            path: "/brands/signup",
            name: "Brand Signup",
            component: () => import("@/views/brands/SignupView.vue"),
            meta: { requiresunAuth: true },
        },
        {
            path: "/suppliers/signup",
            name: "Supplier Signup",
            component: () => import("@/views/suppliers/SignupView.vue"),
            meta: { requiresunAuth: true },
        },
        {
            path: "/dashboard",
            name: "Dashboard",
            component: () => import("@/views/common/RouterViewWrapper.vue"),
            meta: { requiresAuth: true },
            children: [
                {
                    path: "",
                    name: "Dashboard Root",
                    component: () =>
                        import("@/views/common/RouterViewWrapper.vue"),
                    meta: { requiresAuth: true },
                    beforeEnter: (to, from, next) => {
                        const authStore = useAuthStore();
                        const result = handleDashboardRouting(to, authStore);

                        if (!result.redirect) {
                            return next();
                        }

                        return next(result.route);
                    },
                },
                {
                    path: "",
                    name: "Brand Dashboard",
                    component: () => import("@/views/brands/DashboardView.vue"),
                    meta: { requiresAuth: true, hideInMenu: true },
                },
                {
                    path: "",
                    name: "Supplier Dashboard",
                    component: () =>
                        import("@/views/suppliers/DashboardView.vue"),
                    meta: { requiresAuth: true, hideInMenu: true },
                },
                {
                    path: "",
                    name: "Merchant of Record Dashboard",
                    component: () =>
                        import("@/views/merchants-of-record/DashboardView.vue"),
                    meta: { requiresAuth: true, hideInMenu: true },
                },
            ],
        },
        {
            path: "/settings",
            name: "Settings",
            redirect: "/settings/user",
            component: () => import("@/views/common/settings/SettingsView.vue"),
            meta: { requiresAuth: true },
            children: [
                {
                    path: "user",
                    name: "User Settings",
                    component: () =>
                        import("@/views/common/settings/UserSettings.vue"),
                    meta: { requiresAuth: true },
                },
                {
                    path: "company",
                    name: "Company Settings",
                    component: () =>
                        import("@/views/common/settings/CompanySettings.vue"),
                    meta: { requiresAuth: true },
                },
                {
                    path: "team",
                    name: "Team Settings",
                    component: () =>
                        import("@/views/common/settings/TeamSettings.vue"),
                    meta: { requiresAuth: true },
                },
                {
                    path: "addresses",
                    name: "Address Settings",
                    component: () =>
                        import("@/views/common/settings/AddressSettings.vue"),
                    meta: { requiresAuth: true },
                },
                {
                    path: "notifications",
                    name: "Notification Settings",
                    component: () =>
                        import("@/views/common/RouterViewWrapper.vue"),
                    meta: { requiresAuth: true },
                    beforeEnter: (to, from, next) => {
                        const authStore = useAuthStore();
                        if (authStore.isBrand) {
                            next({
                                name: "Notification Settings Brand",
                                params: to.params,
                                query: to.query,
                            });
                        } else if (authStore.isSupplier) {
                            next({
                                name: "Notification Settings Supplier",
                                params: to.params,
                                query: to.query,
                            });
                        } else {
                            next("/404");
                        }
                    },
                },
                {
                    path: "notifications",
                    name: "Notification Settings Brand",
                    component: () =>
                        import(
                            "@/views/common/settings/BuyerEmailPreferences.vue"
                        ),
                    meta: { requiresAuth: true, hideInMenu: true },
                },
                {
                    path: "notifications",
                    name: "Notification Settings Supplier",
                    component: () =>
                        import("@/views/common/settings/SupplierEmailPref.vue"),
                    meta: { requiresAuth: true, hideInMenu: true },
                },
                {
                    path: "referrals",
                    name: "Referral Settings",
                    component: () =>
                        import("@/views/common/settings/ReferralSettings.vue"),
                    meta: { requiresAuth: true },
                },
            ],
        },
        {
            path: "/change-password",
            name: "Change Password",
            component: () => import("@/views/common/ChangePassword.vue"),
            meta: { requiresAuth: true },
        },
        {
            path: "/brands/requests",
            name: "Brand Inquiries",
            component: () => import("@/views/brands/inquiry/InquiryView.vue"),
            meta: { requiresAuth: true },
            children: [
                {
                    path: "",
                    name: "Brand Inquiries Listing",
                    component: () =>
                        import("@/views/brands/inquiry/InquiryListing.vue"),
                    meta: { requiresAuth: true },
                    beforeEnter: (to, from, next) => {
                        const { isFeatureFlagEnabled } = useFeatureFlag();
                        const isElementHidingEnabled = isFeatureFlagEnabled(
                            "isElementHidingEnabled"
                        );

                        if (isElementHidingEnabled) {
                            next({
                                path: `/dashboard`,
                                replace: true,
                            });
                        } else {
                            next();
                        }
                    },
                },
                {
                    path: "create",
                    name: "Brand Inquiry Creation",
                    component: () =>
                        import("@/views/brands/inquiry/InquiryCreation.vue"),
                    meta: { requiresAuth: true },
                    beforeEnter: (to, from, next) => {
                        const { isFeatureFlagEnabled } = useFeatureFlag();
                        const isElementHidingEnabled = isFeatureFlagEnabled(
                            "isElementHidingEnabled"
                        );

                        if (isElementHidingEnabled) {
                            next({
                                path: `/dashboard`,
                                replace: true,
                            });
                        } else {
                            next();
                        }
                    },
                },
                {
                    path: "details/:inquiryId",
                    name: "Brand Inquiries Details",
                    component: () =>
                        import("@/views/brands/inquiry/InquiryDetails.vue"),
                    props: true,
                    meta: { requiresAuth: true },
                    beforeEnter: (to, from, next) => {
                        const { isFeatureFlagEnabled } = useFeatureFlag();
                        const isElementHidingEnabled = isFeatureFlagEnabled(
                            "isElementHidingEnabled"
                        );

                        if (isElementHidingEnabled) {
                            next({
                                path: `/dashboard`,
                                replace: true,
                            });
                        } else {
                            next();
                        }
                    },
                },
            ],
        },
        {
            path: "/brands/contracts",
            name: "Brand Contracts",
            component: () => import("@/views/brands/contract/ContractView.vue"),
            meta: { requiresAuth: true },
            children: [
                {
                    path: "",
                    name: "Brand Contract Listing",
                    component: () =>
                        import("@/views/brands/contract/ContractListing.vue"),
                    meta: { requiresAuth: true },
                    beforeEnter: (to, from, next) => {
                        const { isFeatureFlagEnabled } = useFeatureFlag();
                        const isElementHidingEnabled = isFeatureFlagEnabled(
                            "isElementHidingEnabled"
                        );

                        if (isElementHidingEnabled) {
                            next({
                                path: `/contracts`,
                                replace: true,
                            });
                        } else {
                            next();
                        }
                    },
                },
                {
                    path: "details/quote/:quoteId/",
                    name: "Brand Contract Details",
                    component: () =>
                        import(
                            "@/views/brands/contract/ContractDetailsContainer.vue"
                        ),
                    props: true,
                    meta: { requiresAuth: true },
                    beforeEnter: (to, from, next) => {
                        const { isFeatureFlagEnabled } = useFeatureFlag();
                        const isElementHidingEnabled = isFeatureFlagEnabled(
                            "isElementHidingEnabled"
                        );

                        if (isElementHidingEnabled) {
                            next({
                                path: `/contracts/${to.params.quoteId}`,
                                replace: true,
                            });
                        } else {
                            next();
                        }
                    },
                },
            ],
        },
        {
            path: "/brands/quotes",
            name: "Brand Quotes",
            component: () => import("@/views/brands/quote/QuoteView.vue"),
            meta: { requiresAuth: true },
            children: [
                {
                    path: "",
                    name: "Brand Quotes Listing",
                    component: () =>
                        import("@/views/brands/quote/QuoteListing.vue"),
                    meta: { requiresAuth: true },
                },
                {
                    path: "details/:quoteId",
                    name: "Brand Quotes Details",
                    component: () =>
                        import("@/views/brands/quote/QuoteDetails.vue"),
                    props: true,
                    meta: { requiresAuth: true },
                },
            ],
        },
        {
            path: "/brands/orders",
            name: "Brand Orders",
            component: () => import("@/views/brands/order/OrderView.vue"),
            meta: { requiresAuth: true },
            children: [
                {
                    path: "",
                    name: "Brand Order Listings",
                    component: () =>
                        import("@/views/brands/order/OrderListing.vue"),
                    meta: { requiresAuth: true },
                    beforeEnter: (to, from, next) => {
                        const { isFeatureFlagEnabled } = useFeatureFlag();
                        const isElementHidingEnabled = isFeatureFlagEnabled(
                            "isElementHidingEnabled"
                        );

                        if (isElementHidingEnabled) {
                            next({
                                path: `/orders`,
                                replace: true,
                            });
                        } else {
                            next();
                        }
                    },
                },
                {
                    path: "details/:orderId",
                    name: "Brand Order Details",
                    component: () =>
                        import(
                            "@/views/brands/order/OrderDetailsContainer.vue"
                        ),
                    props: true,
                    meta: { requiresAuth: true },
                    beforeEnter: (to, from, next) => {
                        const { isFeatureFlagEnabled } = useFeatureFlag();
                        const isElementHidingEnabled = isFeatureFlagEnabled(
                            "isElementHidingEnabled"
                        );

                        if (isElementHidingEnabled) {
                            next({
                                path: `/orders/${to.params.orderId}`,
                                replace: true,
                            });
                        } else {
                            next();
                        }
                    },
                },
                {
                    path: "checkout/:orderId",
                    name: "Brand Quotes Checkout",
                    component: () =>
                        import(
                            "@/views/brands/quote/checkout/CheckoutView.vue"
                        ),
                    props: true,
                    meta: { requiresAuth: true },
                    children: [
                        {
                            path: "",
                            name: "Brand Quotes Checkout Details",
                            props: true,
                            component: () =>
                                import(
                                    "@/views/brands/quote/checkout/CheckoutDetails.vue"
                                ),
                        },
                        {
                            path: "confirmation",
                            name: "Brand Quotes Checkout Confirmation",
                            props: true,
                            component: () =>
                                import(
                                    "@/views/brands/quote/checkout/CheckoutConfirmation.vue"
                                ),
                        },
                    ],
                },
            ],
        },
        {
            path: "/payments",
            name: "Buyer Payments",
            component: () =>
                import("@/views/brands/payments/BuyerPaymentsListing.vue"),
            meta: { requiresAuth: true },
            beforeEnter: (to, from, next) => {
                const authStore = useAuthStore();
                if (authStore.isBrand) {
                    next();
                } else {
                    next("/404");
                }
            },
        },
        {
            path: "/statements",
            name: "Statements",
            component: () =>
                import(
                    "@/views/merchants-of-record/statements/MorStatementsView.vue"
                ),
            meta: { requiresAuth: true },
            beforeEnter: (to, from, next) => {
                const authStore = useAuthStore();
                if (authStore.isMerchantOfRecord) {
                    next();
                } else {
                    next("/404");
                }
            },
            children: [
                {
                    path: "",
                    name: "Statements Listing",
                    component: () =>
                        import(
                            "@/views/merchants-of-record/statements/MorStatementsListing.vue"
                        ),
                },
            ],
        },
        {
            path: "/delivery-timeline",
            name: "Delivery Timeline",
            component: () =>
                import("@/views/brands/order/DeliveryTimelineView.vue"),
            meta: { requiresAuth: true },
            beforeEnter: async (to, from, next) => {
                const authStore = useAuthStore();
                if (!authStore.isBrand) {
                    next("/404");
                    return;
                }
                const { checkFeatureAvailability } = useFeatureFlag();
                const isEnabled = await checkFeatureAvailability(
                    "isOrderTimelineEnabled"
                );
                if (isEnabled) {
                    next();
                } else {
                    next("/404");
                }
            },
        },
        {
            path: "/orders",
            name: "Orders",
            component: () => import("@/views/common/RouterViewWrapper.vue"),
            meta: { requiresAuth: true },

            children: [
                {
                    path: "",
                    name: "Order Listings",
                    component: () =>
                        import("@/views/common/RouterViewWrapper.vue"),
                    meta: { requiresAuth: true },
                    beforeEnter: (to, from, next) => {
                        const authStore = useAuthStore();
                        if (authStore.isBrand) {
                            next({
                                name: "Brand App Order Listings",
                                params: to.params,
                                query: to.query,
                            });
                        } else if (authStore.isSupplier) {
                            next({
                                name: "Supplier App Order Listings",
                                params: to.params,
                                query: to.query,
                            });
                        } else if (authStore.isMerchantOfRecord) {
                            next({
                                name: "MOR App Order Listings",
                                params: to.params,
                                query: to.query,
                            });
                        } else {
                            next("/404");
                        }
                    },
                },
                {
                    path: ":orderId",
                    name: "Order Details",
                    component: () =>
                        import("@/views/common/RouterViewWrapper.vue"),
                    props: true,
                    meta: { requiresAuth: true },
                    beforeEnter: (to, from, next) => {
                        const authStore = useAuthStore();

                        if (authStore.isBrand) {
                            next({
                                name: "Brand App Order Details",
                                params: { orderId: to.params.orderId },
                            });
                        } else if (authStore.isSupplier) {
                            next({
                                name: "Supplier App Order Details",
                                params: { orderId: to.params.orderId },
                            });
                        } else if (authStore.isMerchantOfRecord) {
                            next({
                                name: "MOR App Order Details",
                                params: { orderId: to.params.orderId },
                            });
                        } else {
                            next("/404");
                        }
                    },
                },
                {
                    path: "create",
                    name: "Order Creation",
                    component: () =>
                        import("@/views/common/RouterViewWrapper.vue"),
                    meta: { requiresAuth: true },
                    beforeEnter: (to, from, next) => {
                        const authStore = useAuthStore();

                        if (authStore.isMerchantOfRecord) {
                            next({
                                name: "Merchant Order Creation",
                            });
                        } else {
                            next("/404");
                        }
                    },
                },
                {
                    path: ":orderId/edit",
                    name: "Order Edit",
                    component: () =>
                        import("@/views/common/RouterViewWrapper.vue"),
                    props: true,
                    meta: { requiresAuth: true },
                    beforeEnter: (to, from, next) => {
                        const authStore = useAuthStore();

                        if (authStore.isMerchantOfRecord) {
                            next({
                                name: "Merchant of Record Edit Order",
                                params: { orderId: to.params.orderId },
                            });
                        } else {
                            next("/404");
                        }
                    },
                },
                {
                    path: "",
                    name: "Brand App Order Listings",
                    component: () =>
                        import("@/views/brands/order/AppOrderListing.vue"),
                    meta: { requiresAuth: true, hideInMenu: true },
                },
                {
                    path: "",
                    name: "Supplier App Order Listings",
                    component: () =>
                        import("@/views/suppliers/order/AppOrderListing.vue"),
                    meta: { requiresAuth: true, hideInMenu: true },
                },
                {
                    path: "",
                    name: "MOR App Order Listings",
                    component: () =>
                        import(
                            "@/views/merchants-of-record/orders/OrderListing.vue"
                        ),
                    meta: { requiresAuth: true, hideInMenu: true },
                },
                {
                    path: ":orderId",
                    name: "Brand App Order Details",
                    component: () =>
                        import("@/views/brands/order/AppOrderDetails.vue"),
                    props: true,
                    meta: { requiresAuth: true, hideInMenu: true },
                },
                {
                    path: ":orderId",
                    name: "Supplier App Order Details",
                    component: () =>
                        import("@/views/suppliers/order/AppOrderDetail.vue"),
                    props: true,
                    meta: { requiresAuth: true, hideInMenu: true },
                },
                {
                    path: ":orderId",
                    name: "MOR App Order Details",
                    component: () =>
                        import(
                            "@/views/merchants-of-record/orders/OrderDetails.vue"
                        ),
                    props: true,
                    meta: { requiresAuth: true, hideInMenu: true },
                },
                {
                    path: ":orderId/edit",
                    name: "Merchant of Record Edit Order",
                    component: () =>
                        import(
                            "@/views/merchants-of-record/orders/OrderEdit.vue"
                        ),
                    props: true,
                    meta: { requiresAuth: true, hideInMenu: true },
                },
                {
                    path: "create",
                    name: "Merchant Order Creation",
                    component: () =>
                        import(
                            "@/views/merchants-of-record/orders/OrderCreation.vue"
                        ),
                    meta: { requiresAuth: true, hideInMenu: true },
                },
            ],
        },
        {
            path: "/ingredients",
            name: "Brand Ingredients",
            component: () =>
                import("@/views/brands/ingredients/IngredientView.vue"),
            meta: { requiresAuth: true, breadcrumb: "Ingredients" },
            children: [
                {
                    path: "",
                    name: "Brand Ingredients Listing",
                    component: () =>
                        import(
                            "@/views/brands/ingredients/IngredientListing.vue"
                        ),
                    meta: { requiresAuth: true, breadcrumb: "Ingredients" },
                },
                {
                    path: "details/:ingredientId&isAssigned=:isAssigned",
                    name: "Brand Ingredients Details",
                    component: () =>
                        import(
                            "@/views/brands/ingredients/IngredientDetails.vue"
                        ),
                    props: true,
                    meta: { requiresAuth: true, breadcrumb: "" },
                },
                {
                    path: "edit/:ingredientId&isAssigned=:isAssigned",
                    name: "Brand Edit Ingredient",
                    component: () =>
                        import("@/views/brands/ingredients/AddIngredient.vue"),
                    props: true,
                    meta: { requiresAuth: true, breadcrumb: "" },
                },
                {
                    path: "add-ingredient",
                    name: "Add Ingredient",
                    component: () =>
                        import("@/views/brands/ingredients/AddIngredient.vue"),
                    meta: { requiresAuth: true, breadcrumb: "Add Ingredient" },
                },
            ],
        },
        {
            path: "/suppliers/onboarding",
            name: "Supplier Onboarding",
            component: () => import("@/views/suppliers/OnboardingView.vue"),
            meta: { requiresAuth: true },
        },
        {
            path: "/suppliers/orders",
            name: "Supplier Orders",
            component: () => import("@/views/suppliers/order/OrderView.vue"),
            meta: { requiresAuth: true },
            children: [
                {
                    path: "",
                    name: "Supplier Order Listings",
                    component: () =>
                        import("@/views/suppliers/order/OrderListing.vue"),
                    meta: { requiresAuth: true },
                    beforeEnter: (to, from, next) => {
                        const { isFeatureFlagEnabled } = useFeatureFlag();
                        const isElementHidingEnabled = isFeatureFlagEnabled(
                            "isElementHidingEnabled"
                        );

                        if (isElementHidingEnabled) {
                            next({
                                path: `/orders`,
                                replace: true,
                            });
                        } else {
                            next();
                        }
                    },
                },
                {
                    path: "details/:orderId",
                    name: "Supplier Order Details",
                    component: () =>
                        import("@/views/suppliers/order/OrderDetails.vue"),
                    props: true,
                    meta: { requiresAuth: true },
                    beforeEnter: (to, from, next) => {
                        const { isFeatureFlagEnabled } = useFeatureFlag();
                        const isElementHidingEnabled = isFeatureFlagEnabled(
                            "isElementHidingEnabled"
                        );

                        if (isElementHidingEnabled) {
                            next({
                                path: `/orders/${to.params.orderId}`,
                                replace: true,
                            });
                        } else {
                            next();
                        }
                    },
                },
            ],
        },
        {
            path: "/brands/basket",
            name: "Basket",
            component: () => import("@/views/brands/basket/BasketView.vue"),
            meta: { requiresAuth: true },
            children: [
                {
                    path: "",
                    name: "Basket Details",
                    component: () =>
                        import("@/views/brands/basket/BasketDetails.vue"),
                    meta: { requiresAuth: true },
                },
            ],
        },
        {
            path: "/contracts",
            name: "Agreements",
            component: () => import("@/views/common/RouterViewWrapper.vue"),
            meta: { requiresAuth: true },
            children: [
                {
                    path: "",
                    name: "Agreements Listing",
                    component: () =>
                        import("@/views/common/RouterViewWrapper.vue"),
                    meta: { requiresAuth: true },
                    beforeEnter: (to, from, next) => {
                        const authStore = useAuthStore();

                        if (authStore.isBrand) {
                            next({
                                name: "Brand Agreements Listing",
                                params: to.params,
                                query: to.query,
                                hash: to.hash,
                            });
                        } else if (authStore.isMerchantOfRecord) {
                            next({
                                name: "Merchant Agreements Listing",
                                params: to.params,
                                query: to.query,
                                hash: to.hash,
                            });
                        } else {
                            next("/404");
                        }
                    },
                },
                {
                    path: ":agreementId",
                    name: "Agreement Details",
                    component: () =>
                        import("@/views/common/RouterViewWrapper.vue"),
                    props: true,
                    meta: { requiresAuth: true },
                    beforeEnter: (to, from, next) => {
                        const authStore = useAuthStore();

                        if (authStore.isBrand) {
                            next({
                                name: "Brand Agreement Details",
                                params: { agreementId: to.params.agreementId },
                                hash: to.hash,
                                query: to.query,
                            });
                        } else if (authStore.isMerchantOfRecord) {
                            next({
                                name: "Merchant Agreement Details",
                                params: {
                                    agreementUuid: to.params.agreementId,
                                },
                                hash: to.hash,
                                query: to.query,
                            });
                        } else {
                            next("/404");
                        }
                    },
                },
                {
                    path: "create",
                    name: "Agreement Creation",
                    component: () =>
                        import("@/views/common/RouterViewWrapper.vue"),
                    meta: { requiresAuth: true },
                    beforeEnter: (to, from, next) => {
                        const authStore = useAuthStore();

                        if (authStore.isMerchantOfRecord) {
                            next({
                                name: "Merchant Agreement Creation",
                            });
                        } else {
                            next("/404");
                        }
                    },
                },
                {
                    path: "",
                    name: "Brand Agreements Listing",
                    component: () =>
                        import(
                            "@/views/brands/agreements/AgreementListing.vue"
                        ),
                    meta: { requiresAuth: true, hideInMenu: true },
                },
                {
                    path: ":agreementId",
                    name: "Brand Agreement Details",
                    component: () =>
                        import(
                            "@/views/brands/agreements/AgreementDetailsManager.vue"
                        ),
                    props: true,
                    meta: { requiresAuth: true, hideInMenu: true },
                },
                {
                    path: "",
                    name: "Merchant Agreements Listing",
                    component: () =>
                        import(
                            "@/views/merchants-of-record/agreements/AgreementListing.vue"
                        ),
                    meta: { requiresAuth: true, hideInMenu: true },
                },
                {
                    path: ":agreementUuid",
                    name: "Merchant Agreement Details",
                    component: () =>
                        import(
                            "@/views/merchants-of-record/agreements/AgreementDetail.vue"
                        ),
                    props: true,
                    meta: { requiresAuth: true, hideInMenu: true },
                },
                {
                    path: "create",
                    name: "Merchant Agreement Creation",
                    component: () =>
                        import(
                            "@/views/merchants-of-record/agreements/AgreementCreation.vue"
                        ),
                    props: true,
                    meta: { requiresAuth: true, hideInMenu: true },
                },
            ],
        },
        {
            path: "/order-management-contracts",
            name: "Order Management Contracts",
            component: () =>
                import(
                    "@/views/merchants-of-record/order-management-contracts/OrderManagementContractsView.vue"
                ),
            meta: { requiresAuth: true },
            children: [
                {
                    path: "",
                    name: "Order Management Contracts Listing",
                    component: () =>
                        import("@/views/common/RouterViewWrapper.vue"),
                    meta: { requiresAuth: true },
                    beforeEnter: (to, from, next) => {
                        const authStore = useAuthStore();

                        if (authStore.isMerchantOfRecord) {
                            next({
                                name: "Merchant of Record Order Management Contracts Listing",
                                params: to.params,
                                query: to.query,
                                hash: to.hash,
                            });
                        } else {
                            next("/404");
                        }
                    },
                },
                {
                    path: "",
                    name: "Merchant of Record Order Management Contracts Listing",
                    component: () =>
                        import(
                            "@/views/merchants-of-record/order-management-contracts/OrderManagementContractsListing.vue"
                        ),
                    props: true,
                    meta: { requiresAuth: true, hideInMenu: true },
                },
                {
                    path: ":contractUuid",
                    name: "Merchant of Record Order Management Contract Details",
                    component: () =>
                        import(
                            "@/views/merchants-of-record/order-management-contracts/OrderManagementContractDetail.vue"
                        ),
                    props: true,
                    meta: { requiresAuth: true, hideInMenu: true },
                },
            ],
        },
        {
            path: "/products",
            name: "Supplier Product Listings",
            component: () => import("@/views/suppliers/ProductsListing.vue"),
            meta: { requiresAuth: true },
        },
        {
            path: "/suppliers/requests",
            name: "Supplier Inquiries",
            component: () =>
                import("@/views/suppliers/inquiry/InquiryView.vue"),
            meta: { requiresAuth: true },
            children: [
                {
                    path: "",
                    name: "Supplier Inquiries Listings",
                    component: () =>
                        import("@/views/suppliers/inquiry/InquiryListing.vue"),
                    meta: { requiresAuth: true },
                    beforeEnter: (to, from, next) => {
                        const { isFeatureFlagEnabled } = useFeatureFlag();
                        const isElementHidingEnabled = isFeatureFlagEnabled(
                            "isElementHidingEnabled"
                        );

                        if (isElementHidingEnabled) {
                            next({
                                path: `/dashboard`,
                                replace: true,
                            });
                        } else {
                            next();
                        }
                    },
                },
                {
                    path: "details/:inquiryId",
                    name: "Supplier Inquiry Details",
                    component: () =>
                        import("@/views/suppliers/inquiry/InquiryDetails.vue"),
                    props: true,
                    meta: { requiresAuth: true },
                    beforeEnter: (to, from, next) => {
                        const { isFeatureFlagEnabled } = useFeatureFlag();
                        const isElementHidingEnabled = isFeatureFlagEnabled(
                            "isElementHidingEnabled"
                        );

                        if (isElementHidingEnabled) {
                            next({
                                path: `/dashboard`,
                                replace: true,
                            });
                        } else {
                            next();
                        }
                    },
                },
            ],
        },
        {
            path: "/suppliers/quotes",
            name: "Supplier Quotes",
            component: () => import("@/views/suppliers/quote/QuoteView.vue"),
            meta: { requiresAuth: true },
            children: [
                {
                    path: "",
                    name: "Suppliers Quote Listing",
                    component: () =>
                        import("@/views/suppliers/quote/QuoteListing.vue"),
                    meta: { requiresAuth: true },
                },
                {
                    path: "create/:inquiryId",
                    name: "Supplier Quote Creation",
                    component: () =>
                        import("@/views/suppliers/quote/QuoteCreation.vue"),
                    props: true,
                    meta: { requiresAuth: true },
                },
                {
                    path: "details/:quoteId",
                    name: "Supplier Quote Details",
                    component: () =>
                        import("@/views/suppliers/quote/QuoteDetails.vue"),
                    props: true,
                    meta: { requiresAuth: true },
                },
                {
                    path: "edit/:quoteId",
                    name: "Supplier Edit Quote",
                    component: () =>
                        import("@/views/suppliers/quote/QuoteEdit.vue"),
                    props: true,
                    meta: { requiresAuth: true },
                },
            ],
        },
        {
            path: "/suppliers/contracts",
            name: "Supplier Contracts",
            component: () =>
                import("@/views/suppliers/contract/ContractView.vue"),
            meta: { requiresAuth: true },
            children: [
                {
                    path: "",
                    name: "Supplier Contract Listing",
                    component: () =>
                        import(
                            "@/views/suppliers/contract/ContractListing.vue"
                        ),
                    meta: { requiresAuth: true },
                },
                {
                    path: "details/quote/:quoteId/",
                    name: "Supplier Contract Details",
                    component: () =>
                        import(
                            "@/views/suppliers/contract/ContractDetails.vue"
                        ),
                    props: true,
                    meta: { requiresAuth: true },
                },
                {
                    path: "contract-creation/:quoteId",
                    name: "Contract Creation",
                    component: () =>
                        import(
                            "@/views/suppliers/contract/ContractCreation.vue"
                        ),
                    props: true,
                    meta: { requiresAuth: true, breadcrumb: "" },
                },
                {
                    path: "edit-creation/:quoteId?=:edit",
                    name: "Edit Contract",
                    component: () =>
                        import(
                            "@/views/suppliers/contract/ContractCreation.vue"
                        ),
                    props: true,
                    meta: { requiresAuth: true, breadcrumb: "" },
                },
            ],
        },
        {
            path: "/suppliers/kyb",
            name: "Suppliers KYB",
            component: () => import("@/views/suppliers/kyb/KybView.vue"),
            meta: { requiresAuth: true },
            children: [
                {
                    path: "",
                    name: "Supplier Company Details",
                    component: () =>
                        import("@/views/suppliers/kyb/KybGeneral.vue"),
                    meta: { requiresAuth: true },
                },
                {
                    path: "authorised-user",
                    name: "Supplier Authorised User",
                    component: () =>
                        import("@/views/suppliers/kyb/KybAuthorization.vue"),
                    meta: { requiresAuth: true },
                },
                {
                    path: "shareholders",
                    name: "Supplier Shareholders",
                    component: () =>
                        import("@/views/suppliers/kyb/KybShareholders.vue"),
                    meta: { requiresAuth: true },
                },
                {
                    path: "thank-you",
                    name: "Supplier Thank You",
                    component: () =>
                        import("@/views/suppliers/kyb/KybThankYou.vue"),
                    meta: { requiresAuth: true },
                },
                {
                    path: "get-paid",
                    name: "Supplier Get Paid",
                    component: () =>
                        import("@/views/suppliers/kyb/KybGetPaid.vue"),
                    meta: { requiresAuth: true },
                },
            ],
        },
        {
            path: "/tools/sugar-contract-calculator",
            name: "Sugar Contract Calculator",
            component: () =>
                import(
                    "@/views/tools/SugarContractCalculator/SugarContractCalculator.vue"
                ),
        },
        {
            path: "/buyers",
            name: "Merchant of Record Buyers",
            component: () =>
                import("@/views/merchants-of-record/buyers/BuyersView.vue"),
            meta: { requiresAuth: true },
            beforeEnter: (to, from, next) => {
                const authStore = useAuthStore();
                if (authStore.isMerchantOfRecord) {
                    next();
                } else {
                    next("/404");
                }
            },
            children: [
                {
                    path: "",
                    name: "Merchant of Record Buyer Listings",
                    component: () =>
                        import(
                            "@/views/merchants-of-record/buyers/BuyerListView.vue"
                        ),
                },
                {
                    path: ":buyerUuid",
                    name: "Merchant of Record Buyer Details",
                    props: true,
                    component: () =>
                        import(
                            "@/views/merchants-of-record/buyers/BuyerDetailsView.vue"
                        ),
                },
                {
                    path: ":buyerUuid/credit-notes",
                    name: "MOR Buyer Credit Notes",
                    props: true,
                    component: () =>
                        import(
                            "@/views/merchants-of-record/buyers/BuyerCreditNotesView.vue"
                        ),
                    meta: { requiresAuth: true, hideInMenu: true },
                },
                {
                    path: ":buyerUuid/suppliers/:supplierUuid",
                    name: "Merchant of Record Buyer Supplier Details",
                    props: true,
                    component: () =>
                        import(
                            "@/views/merchants-of-record/buyers/BuyerSupplierDetailsView.vue"
                        ),
                },
                {
                    path: ":buyerUuid/supplier-submissions/:submissionId",
                    name: "Merchant of Record Buyer Supplier Submission Details",
                    props: true,
                    component: () =>
                        import(
                            "@/views/merchants-of-record/buyer-supplier-submissions/SupplierSubmissionDetailView.vue"
                        ),
                    meta: { requiresAuth: true, hideInMenu: true },
                    beforeEnter: (to, from, next) => {
                        const authStore = useAuthStore();
                        if (authStore.isMerchantOfRecord) {
                            next();
                        } else {
                            next("/404");
                        }
                    },
                },
            ],
        },
        {
            path: "/buyer-onboarding",
            name: "Merchant of Record Buyer Onboarding",
            component: () =>
                import(
                    "@/views/merchants-of-record/buyer-onboarding/BuyerOnboardingView.vue"
                ),
            meta: { requiresAuth: true },
            beforeEnter: (to, from, next) => {
                const authStore = useAuthStore();
                if (authStore.isMerchantOfRecord) {
                    next();
                } else {
                    next("/404");
                }
            },
            children: [
                {
                    path: "",
                    name: "Merchant of Record Buyer Onboarding Listings",
                    component: () =>
                        import(
                            "@/views/merchants-of-record/buyer-onboarding/BuyerOnboardingListView.vue"
                        ),
                },
                {
                    path: ":buyerUuid",
                    name: "Merchant of Record Buyer Onboarding Details",
                    props: true,
                    component: () =>
                        import(
                            "@/views/merchants-of-record/buyer-onboarding/BuyerOnboardingDetailsView.vue"
                        ),
                },
            ],
        },
        {
            path: "/buyer-supplier-submissions",
            name: "Merchant of Record Supplier Submissions",
            component: () =>
                import(
                    "@/views/merchants-of-record/buyer-supplier-submissions/SupplierSubmissionsView.vue"
                ),
            meta: { requiresAuth: true },
            beforeEnter: (to, from, next) => {
                const authStore = useAuthStore();
                if (authStore.isMerchantOfRecord) {
                    next();
                } else {
                    next("/404");
                }
            },
            children: [
                {
                    path: "",
                    name: "Merchant of Record Supplier Submissions Listings",
                    component: () =>
                        import(
                            "@/views/merchants-of-record/buyer-supplier-submissions/SupplierSubmissionsListView.vue"
                        ),
                },
                {
                    path: ":submissionId",
                    name: "Merchant of Record Supplier Submission Details",
                    props: true,
                    component: () =>
                        import(
                            "@/views/merchants-of-record/buyer-supplier-submissions/SupplierSubmissionDetailView.vue"
                        ),
                },
            ],
        },
        {
            path: "/opply-documents",
            name: "Opply Documents",
            component: () =>
                import(
                    "@/views/merchants-of-record/opply-documents/OpplyDocuments.vue"
                ),
            meta: { requiresAuth: true },
            beforeEnter: (to, from, next) => {
                const authStore = useAuthStore();
                if (authStore.isMerchantOfRecord) {
                    next();
                } else {
                    next("/404");
                }
            },
        },
        {
            path: "/agents",
            name: "Agents",
            component: () =>
                import("@/views/merchants-of-record/agents/AgentsView.vue"),
            meta: { requiresAuth: true },
            beforeEnter: (to, from, next) => {
                const authStore = useAuthStore();
                if (authStore.isMerchantOfRecord) {
                    next();
                } else {
                    next("/404");
                }
            },
        },
        {
            path: "/admin/users",
            name: "MoR Users",
            component: () =>
                import(
                    "@/views/merchants-of-record/admin/users/MorUsersView.vue"
                ),
            meta: { requiresAuth: true },
            beforeEnter: (to, from, next) => {
                const authStore = useAuthStore();
                if (authStore.isMerchantOfRecord) {
                    next();
                } else {
                    next("/404");
                }
            },
        },
        {
            path: "/tasks",
            name: "Tasks",
            component: () =>
                import("@/views/merchants-of-record/tasks/TasksView.vue"),
            meta: { requiresAuth: true },
            beforeEnter: (to, from, next) => {
                const authStore = useAuthStore();
                const { isFeatureFlagEnabled } = useFeatureFlag();

                if (
                    authStore.isMerchantOfRecord &&
                    isFeatureFlagEnabled("tasksEnhancementEnabled")
                ) {
                    next();
                } else {
                    next("/404");
                }
            },
        },
        {
            path: "/tasks/:taskUuid",
            name: "Task Detail",
            component: () =>
                import("@/views/merchants-of-record/tasks/TaskDetailView.vue"),
            props: true,
            meta: { requiresAuth: true },
            beforeEnter: (to, from, next) => {
                const authStore = useAuthStore();
                const { isFeatureFlagEnabled } = useFeatureFlag();

                if (
                    authStore.isMerchantOfRecord &&
                    isFeatureFlagEnabled("tasksEnhancementEnabled")
                ) {
                    next();
                } else {
                    next("/404");
                }
            },
        },
        {
            path: "/crm/companies",
            name: "CRM Companies",
            component: () =>
                import("@/views/merchants-of-record/crm/CrmCompaniesView.vue"),
            meta: { requiresAuth: true },
            beforeEnter: (to, from, next) => {
                const authStore = useAuthStore();
                if (authStore.isMerchantOfRecord) {
                    next();
                } else {
                    next("/404");
                }
            },
            children: [
                {
                    path: "",
                    name: "CRM Lead Generation",
                    component: () =>
                        import(
                            "@/views/merchants-of-record/crm/LeadGenerationView.vue"
                        ),
                },
                {
                    path: "import",
                    redirect: { name: "CRM File Import" },
                },
                {
                    path: "import-file",
                    name: "CRM File Import",
                    component: () =>
                        import(
                            "@/views/merchants-of-record/crm/FileImportView.vue"
                        ),
                },
                {
                    path: "lead-generation",
                    redirect: { name: "CRM Lead Generation" },
                },
                {
                    path: "background-processes",
                    redirect: { name: "CRM Background Processes" },
                },
            ],
        },
        {
            path: "/crm/background-processes",
            name: "CRM Background Processes",
            component: () =>
                import(
                    "@/views/merchants-of-record/crm/BackgroundProcessesView.vue"
                ),
            meta: { requiresAuth: true },
            beforeEnter: (to, from, next) => {
                const authStore = useAuthStore();
                if (authStore.isMerchantOfRecord) {
                    next();
                } else {
                    next("/404");
                }
            },
        },
        {
            path: "/crm/contacts",
            name: "CRM Contacts",
            component: () =>
                import("@/views/merchants-of-record/crm/CrmContactsView.vue"),
            meta: { requiresAuth: true },
            beforeEnter: (to, from, next) => {
                const authStore = useAuthStore();
                if (authStore.isMerchantOfRecord) {
                    next();
                } else {
                    next("/404");
                }
            },
            children: [
                {
                    path: "",
                    name: "CRM Contacts Listing",
                    component: () =>
                        import(
                            "@/views/merchants-of-record/crm/ContactListView.vue"
                        ),
                },
                {
                    path: "import",
                    name: "CRM Contact Import",
                    component: () =>
                        import(
                            "@/views/merchants-of-record/crm/ContactImportView.vue"
                        ),
                },
            ],
        },
        {
            path: "/suppliers",
            name: "Suppliers",
            component: () => import("@/views/common/RouterViewWrapper.vue"),
            meta: { requiresAuth: true },
            children: [
                {
                    path: "",
                    name: "Supplier Listings",
                    component: () =>
                        import("@/views/common/RouterViewWrapper.vue"),
                    meta: { requiresAuth: true },
                    beforeEnter: (to, from, next) => {
                        const authStore = useAuthStore();
                        if (authStore.isBrand) {
                            next({
                                name: "Brand My Suppliers",
                                params: to.params,
                                hash: to.hash,
                                query: to.query,
                            });
                        } else if (authStore.isMerchantOfRecord) {
                            next({
                                name: "Merchant of Record Supplier Listings",
                                params: to.params,
                                query: to.query,
                            });
                        } else {
                            next("/404");
                        }
                    },
                },
                {
                    path: "",
                    name: "Brand My Suppliers",
                    component: () =>
                        import("@/views/brands/my-suppliers/SuppliersList.vue"),
                    meta: { requiresAuth: true, hideInMenu: true },
                },
                {
                    path: "",
                    name: "Merchant of Record Supplier Listings",
                    component: () =>
                        import(
                            "@/views/merchants-of-record/suppliers/SupplierListing.vue"
                        ),
                    meta: { requiresAuth: true, hideInMenu: true },
                },
                {
                    path: ":id/details",
                    name: "Brand My Supplier Details",
                    component: () =>
                        import(
                            "@/views/brands/my-suppliers/SupplierDetails.vue"
                        ),
                    props: true,
                    meta: { requiresAuth: true, hideInMenu: true },
                    beforeEnter: (to, from, next) => {
                        const authStore = useAuthStore();
                        if (authStore.isBrand) {
                            next();
                        } else {
                            next("/404");
                        }
                    },
                },
                {
                    path: ":supplierUuid",
                    name: "MOR Supplier Details",
                    props: true,
                    component: () =>
                        import(
                            "@/views/merchants-of-record/suppliers/SupplierDetails.vue"
                        ),
                    meta: { requiresAuth: true, hideInMenu: true },
                    beforeEnter: (to, from, next) => {
                        const authStore = useAuthStore();
                        if (authStore.isMerchantOfRecord) {
                            next();
                        } else {
                            next("/404");
                        }
                    },
                },
            ],
        },
        {
            path: "/onboarding-suppliers",
            component: () => import("@/views/common/RouterViewWrapper.vue"),
            meta: { requiresAuth: true, hideInMenu: true },
            children: [
                {
                    path: ":uuid",
                    name: "Onboarding Supplier Details",
                    props: true,
                    component: () =>
                        import(
                            "@/views/merchants-of-record/suppliers/OnboardingSupplierDetails.vue"
                        ),
                    meta: { requiresAuth: true, hideInMenu: true },
                    beforeEnter: (to, from, next) => {
                        const authStore = useAuthStore();

                        if (authStore.isMerchantOfRecord || authStore.isBrand) {
                            next();
                        } else {
                            next("/404");
                        }
                    },
                },
                {
                    path: ":uuid/create-final-supplier",
                    name: "Create Final Supplier",
                    props: true,
                    component: () =>
                        import(
                            "@/views/merchants-of-record/suppliers/CreateFinalSupplier.vue"
                        ),
                    meta: { requiresAuth: true, hideInMenu: true },
                    beforeEnter: (to, from, next) => {
                        const authStore = useAuthStore();

                        if (authStore.isMerchantOfRecord) {
                            next();
                        } else {
                            next("/404");
                        }
                    },
                },
            ],
        },
        {
            path: "/tutorials",
            name: "Buyer Tutorials",
            component: () =>
                import("@/views/brands/tutorials/TutorialsView.vue"),
            meta: { requiresAuth: true },
            beforeEnter: (to, from, next) => {
                const authStore = useAuthStore();
                if (authStore.isBrand) {
                    next();
                } else {
                    next("/404");
                }
            },
            children: [
                {
                    path: "",
                    name: "Buyer Tutorials Page",
                    component: () =>
                        import("@/views/brands/tutorials/BuyerTutorials.vue"),
                },
            ],
        },
        {
            path: "/documents",
            name: "Buyer Documents",
            component: () =>
                import("@/views/brands/documents/BuyerDocuments.vue"),
            meta: { requiresAuth: true },
            beforeEnter: (to, from, next) => {
                const authStore = useAuthStore();
                if (authStore.isBrand) {
                    next();
                } else {
                    next("/404");
                }
            },
        },
        {
            path: "/tools/opplymiser",
            name: "Opplymiser",
            component: () =>
                import("@/views/tools/Opplymiser/OpplymiserView.vue"),
            children: [
                {
                    path: "",
                    name: "Opplymiser Calculations",
                    component: () =>
                        import("@/views/tools/Opplymiser/Calculations.vue"),
                },
                {
                    path: "results",
                    name: "Opplymiser Results",
                    component: () =>
                        import("@/views/tools/Opplymiser/Results.vue"),
                },
            ],
        },
        {
            path: "/404",
            name: "404 Page",
            component: () => import("@/views/common/404Page.vue"),
        },
        {
            path: "/:notFound(.*)",
            redirect: "/404",
        },
    ],
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        }
        return { left: 0, top: 0 };
    },
});

router.onError((error, to) => {
    const message = error instanceof Error ? error.message : String(error);
    if (
        message.includes("Failed to fetch dynamically imported module") ||
        message.includes("Importing a module script failed")
    ) {
        window.location.href = to.fullPath;
    }
});

router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore();
    // Set page loading to true to show the whole page loader
    authStore.pageLoading = true;

    function getDashboardRoute() {
        if (authStore.isBrand) {
            return "Brand Dashboard";
        }

        if (authStore.isSupplier) {
            return "Supplier Dashboard";
        }

        if (authStore.isMerchantOfRecord) {
            return "Merchant of Record Dashboard";
        }

        return "Signin";
    }

    // Check if the route requires authentication
    if (to.meta.requiresAuth) {
        // Ensure user data is loaded
        if (!authStore.userData || !authStore.companyData) {
            await authStore.authenticateUser();
        }

        if (authStore.auth) {
            return next();
        }

        return next({
            path: "/signin",
            query: { redirectTo: to.fullPath, ...to.query },
        });
    }

    // Check if the route is for non-authenticated users only
    if (to.meta.requiresunAuth) {
        // If no auth token exists, allow access immediately
        if (!authStore.token) {
            return next();
        }

        // Skip auth check for admin masquerade
        if (to.query.adminMasqueradeToken) {
            return next();
        }

        try {
            // Only attempt auth if we have a token but missing user data
            if (!authStore.userData || !authStore.companyData) {
                await authStore.authenticateUser();
            }

            // If still authenticated after check, redirect to dashboard
            if (authStore.auth) {
                return next({ name: getDashboardRoute() });
            }
        } catch {
            // On auth failure, reset store and allow access
            authStore.$reset();
        }
        return next();
    }
    // For routes that don't require specific auth checks
    return next();
});

router.afterEach(() => {
    const authStore = useAuthStore();
    // Set page loading to false to hide the whole page loader
    authStore.pageLoading = false;
});

export default router;
